import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { XLineIcon } from '@/common/assets/icon/icon';

interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  title: string;
  headerDescription?: string;
  children: React.ReactNode;
  autoClose?: boolean;
  maxWidth?: string;
  minWidth?: string;
  scrollable?: boolean;
  footer?: React.ReactNode;
  zIndex?: number;
}

const ModalCenter: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  headerDescription, 
  children, 
  footer, 
  maxWidth,
  minWidth,
  autoClose = true, 
  scrollable = true,
  zIndex = 1000
}) => {

  useEffect(() => {
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (autoClose) {
          onClose && onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  if (!isOpen) return null;

  // minWidth 기본값은 항상 600px 유지, maxWidth가 있을 때 CSS min()으로 충돌 방지
  const effectiveMinWidth = minWidth ?? '600px';

  // Portal을 사용하여 body에 직접 렌더링 (부모의 transform/animation 영향 방지)
  return createPortal(
    <ModalOverlay onClick={autoClose ? onClose : undefined} $zIndex={zIndex}>
      <ModalContent onClick={(e) => e.stopPropagation()} style={{
        maxWidth,
        minWidth: maxWidth ? `min(${effectiveMinWidth}, ${maxWidth})` : effectiveMinWidth
      }}>
        <ModalHeader>
          <FlexColumn>
            <Title>{title}</Title>
            {headerDescription && <Description>{headerDescription}</Description>}
          </FlexColumn>
          {onClose && (
            <CloseButton onClick={onClose}>
              <XLineIcon size={16}/>
            </CloseButton>
          )}
        </ModalHeader>
        <ModalBody $scrollable={scrollable}>{children}</ModalBody>
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

const ModalOverlay = styled.div<{ $zIndex?: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${props => props.$zIndex || 1000};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${semanticColors.background.secondary};
  border-radius: 5px;
  max-height: 90vh;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  min-height: 70px;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1rem 1rem 1rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const Title = styled.span`
  ${typography.heading.h6};
  color: ${semanticColors.text.primary};
`;

const CloseButton = styled.button`
  position: absolute;
  right: 1rem;
  top: 1.5rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
`;

const ModalBody = styled.div<{$scrollable: boolean}>`
  flex: 1;
  min-height: 0;
  transition: all 0.3s ease-in-out;
  overflow-y: ${props => props.$scrollable ? 'auto' : 'hidden'};
  overflow-x: hidden;
`;

const ModalFooter = styled.div`
  min-height: 80px;
  padding: 1rem;
  border-top: 1px solid ${semanticColors.border.primary};
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`; 

const Description = styled.p`
  ${typography.caption.caption1.default};
  color: ${semanticColors.text.secondary};
`;

export default ModalCenter;
